import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import FilterBar, { FilterSummary } from '../components/FilterBar';
import DisclaimerFooter from '../components/DisclaimerFooter';
import { erscData } from '../data/erscData';
import './ErscRecommendations.css';
import { usePersistentFlag, usePersistentSet } from '../utils/expandedState';

const STATUSES = [
  { value: 'COMPLETE', label: 'Complete' },
  { value: 'IN PROGRESS', label: 'In Progress' },
  { value: 'INCORPORATED INTO MOU', label: 'Inc. into MOU' },
  { value: 'NONE', label: 'No Status' },
];

const RESPONSIBLES = [
  { value: 'Select Board', label: 'Select Board' },
  { value: 'School Committee', label: 'School Committee' },
  { value: 'Select Board Office', label: 'SB Office' },
];

function getAllRecommendations(data) {
  const recs = [];
  data.sections.forEach(section => {
    section.subsections.forEach(sub => {
      sub.recommendations.forEach(rec => {
        recs.push(rec);
      });
    });
  });
  return recs;
}

function ErscRecommendations() {
  const navigate = useNavigate();
  const [sel, setSel] = useState({ status: new Set(), responsible: new Set() });
  const [expandedSections, setExpandedSections] = usePersistentSet('ersc:expandedSections', () => new Set(erscData.sections.map(s => s.id)));
  const [expandedSubsections, setExpandedSubsections] = usePersistentSet('ersc:expandedSubsections');
  const [expandedRecs, setExpandedRecs] = usePersistentSet('ersc:expandedRecs');
  const [overviewExpanded, setOverviewExpanded] = usePersistentFlag('ersc:overviewExpanded');
  const [statusKeyExpanded, setStatusKeyExpanded] = usePersistentFlag('ersc:statusKeyExpanded');

  useEffect(() => {
    document.title = 'Expenditures & Revenues Study Committee Recommendations';
  }, []);

  const allRecs = useMemo(() => getAllRecommendations(erscData), []);

  const dimensions = useMemo(() => [
    { key: 'status', label: 'Status', values: STATUSES.map(s => ({
      value: s.value, label: s.label,
      count: s.value === 'NONE' ? allRecs.filter(r => !r.status).length : allRecs.filter(r => r.status === s.value).length,
    })) },
    { key: 'responsible', label: 'Responsible', values: RESPONSIBLES.map(r => ({
      value: r.value, label: r.label,
      count: allRecs.filter(x => x.responsible && x.responsible.includes(r.value)).length,
    })) },
  ], [allRecs]);

  const toggleValue = (key, value) => setSel(prev => {
    const next = new Set(prev[key]);
    if (next.has(value)) next.delete(value); else next.add(value);
    return { ...prev, [key]: next };
  });
  const clearDimension = (key) => setSel(prev => ({ ...prev, [key]: new Set() }));
  const clearAllFilters = () => setSel({ status: new Set(), responsible: new Set() });

  const matchesRec = useMemo(() => {
    return (rec) => {
      if (sel.status.size && !sel.status.has(rec.status || 'NONE')) return false;
      if (sel.responsible.size && !(rec.responsible && [...sel.responsible].some(r => rec.responsible.includes(r)))) return false;
      return true;
    };
  }, [sel]);

  const filteredSections = useMemo(() => {
    return erscData.sections.map(section => ({
      ...section,
      subsections: section.subsections.map(sub => ({
        ...sub,
        recommendations: sub.recommendations.filter(matchesRec)
      })).filter(sub => sub.recommendations.length > 0)
    })).filter(section => section.subsections.length > 0);
  }, [matchesRec]);

  const totalFilteredRecs = useMemo(() => {
    return filteredSections.reduce((sum, section) =>
      sum + section.subsections.reduce((s2, sub) =>
        s2 + sub.recommendations.length, 0), 0);
  }, [filteredSections]);


  const toggleSection = (sectionId) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  const toggleSubsection = (subId) => {
    setExpandedSubsections(prev => {
      const next = new Set(prev);
      if (next.has(subId)) next.delete(subId);
      else next.add(subId);
      return next;
    });
  };

  const toggleRec = (recId) => {
    setExpandedRecs(prev => {
      const next = new Set(prev);
      if (next.has(recId)) next.delete(recId);
      else next.add(recId);
      return next;
    });
  };

  const expandAll = () => {
    setExpandedSections(new Set(erscData.sections.map(s => s.id)));
    const allSubIds = new Set();
    const allRecIds = new Set();
    erscData.sections.forEach(section => {
      section.subsections.forEach(sub => {
        allSubIds.add(sub.id);
        sub.recommendations.forEach(rec => allRecIds.add(rec.id));
      });
    });
    setExpandedSubsections(allSubIds);
    setExpandedRecs(allRecIds);
    setOverviewExpanded(true);
    setStatusKeyExpanded(true);
  };

  const collapseAll = () => {
    setExpandedSections(new Set());
    setExpandedSubsections(new Set());
    setExpandedRecs(new Set());
    setOverviewExpanded(false);
    setStatusKeyExpanded(false);
  };

  const statusClass = (status) => {
    if (status === 'COMPLETE') return 'ersc-status-complete';
    if (status === 'IN PROGRESS') return 'ersc-status-in-progress';
    if (status === 'INCORPORATED INTO MOU') return 'ersc-status-mou';
    return '';
  };

  const statusLabel = (status) => {
    if (status === 'COMPLETE') return 'Complete';
    if (status === 'IN PROGRESS') return 'In Progress';
    if (status === 'INCORPORATED INTO MOU') return 'Inc. into MOU';
    return '';
  };

  const renderRecMetadata = (rec) => (
    <div className="ersc-rec-metadata">
      <div className="ersc-meta-row">
        {rec.status && (
          <span className={`ersc-status-badge ${statusClass(rec.status)}`}>
            {statusLabel(rec.status)}
          </span>
        )}
        {rec.responsible && rec.responsible.length > 0 && (
          <span className="ersc-meta-item">
            <span className="ersc-meta-label">Responsible:</span>{' '}
            {rec.responsible.join(' · ')}
          </span>
        )}
      </div>
      {rec.statusDetail && (
        <div className="ersc-status-detail">
          <span className="ersc-meta-label">Status — </span>
          {rec.statusDetail}
        </div>
      )}
      {rec.roadmapRef && (
        <div className="ersc-roadmap-ref">
          <span className="ersc-roadmap-badge">2030 Roadmap</span>
          <span dangerouslySetInnerHTML={{ __html: rec.roadmapRef }} />
        </div>
      )}
    </div>
  );

  const renderSubItems = (subItems) => (
    <div className="ersc-sub-items">
      {subItems.map(item => (
        <div key={item.id} className="ersc-sub-item">
          <div className="ersc-sub-item-header">
            <span className="ersc-sub-item-id">{item.id}</span>
            <span className="ersc-sub-item-title">{item.title}</span>
            {item.status && (
              <span className={`ersc-status-badge small ${statusClass(item.status)}`}>
                {statusLabel(item.status)}
              </span>
            )}
          </div>
          {item.description && (
            <p className="ersc-sub-item-desc">{item.description}</p>
          )}
          {item.statusDetail && (
            <div className="ersc-status-detail small">
              <span className="ersc-meta-label">Status — </span>
              {item.statusDetail}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  // A roadmapRef may carry an authored <a href> into the 2030 Roadmap (e.g.
  // IV.4 -> strategy 3.A). Those links are plain HTML the router never sees, so
  // route same-site ones through it and leave the rest to the browser.
  const handleRefLinkClick = (e) => {
    const link = e.target.closest('a[href]');
    if (!link) return;
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const url = new URL(link.href, window.location.origin);
    if (url.origin !== window.location.origin) return;
    e.preventDefault();
    navigate(url.pathname + url.search + url.hash);
  };

  return (
    <div className="ersc-page">
      <a href="#main-content" className="ersc-skip-link">Skip to main content</a>
      <Header
        title="Expenditures & Revenues Study Committee Recommendations"
        creditExtra="website design by Michael Rubenstein"
      />

      <main id="main-content" tabIndex={-1} className="ersc-content">
        <div className="ersc-intro">
          <p className="ersc-updated">Final Report — {erscData.updatedDate}</p>
        </div>

        <div className="ersc-controls">
          <FilterBar
            allCount={allRecs.length}
            dimensions={dimensions}
            selected={sel}
            onToggleValue={toggleValue}
            onClearDimension={clearDimension}
            onClearAll={clearAllFilters}
            trailing={
              <>
                <button className="ersc-expand-btn" onClick={expandAll}>Expand All</button>
                <button className="ersc-expand-btn" onClick={collapseAll}>Collapse All</button>
              </>
            }
          />
        </div>

        <FilterSummary
          count={totalFilteredRecs}
          noun="recommendation"
          nounPlural="recommendations"
          dimensions={dimensions}
          selected={sel}
          onClearAll={clearAllFilters}
        />

        <div className="ersc-preamble-sections">
          <div className="ersc-preamble">
            <button
              className={`ersc-preamble-header ${overviewExpanded ? 'expanded' : ''}`}
              onClick={() => setOverviewExpanded(!overviewExpanded)}
              aria-expanded={overviewExpanded}
            >
              <span className={`ersc-chevron${overviewExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
              <span className="ersc-preamble-title">About This Document</span>
            </button>
            {overviewExpanded && (
              <div className="ersc-preamble-body">
                {erscData.introduction.overview.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            )}
          </div>

          <div className="ersc-preamble">
            <button
              className={`ersc-preamble-header ${statusKeyExpanded ? 'expanded' : ''}`}
              onClick={() => setStatusKeyExpanded(!statusKeyExpanded)}
              aria-expanded={statusKeyExpanded}
            >
              <span className={`ersc-chevron${statusKeyExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
              <span className="ersc-preamble-title">Status Key & Notes</span>
            </button>
            {statusKeyExpanded && (
              <div className="ersc-preamble-body">
                <p><strong>Status key:</strong> {erscData.introduction.statusKey}</p>
                <p><strong>2030 Roadmap cross-references:</strong> {erscData.introduction.roadmapNote}</p>
                <p><strong>Responsibility tags:</strong> {erscData.introduction.responsibilityNote}</p>
              </div>
            )}
          </div>
        </div>

        <div className="ersc-sections" onClick={handleRefLinkClick}>
          {filteredSections.map(section => (
            <div key={section.id} className="ersc-section">
              <h2 className="ersc-heading"><button
                className={`ersc-section-header ${expandedSections.has(section.id) ? 'expanded' : ''}`}
                onClick={() => toggleSection(section.id)}
                aria-expanded={expandedSections.has(section.id)}
              >
                <span className={`ersc-chevron ersc-chevron-light${expandedSections.has(section.id) ? ' expanded' : ''}`} aria-hidden="true"></span>
                <div className="ersc-section-header-text">
                  <span className="ersc-section-name">{section.id}. {section.name}</span>
                  {section.description && (
                    <span className="ersc-section-desc">{section.description}</span>
                  )}
                </div>
              </button></h2>

              {expandedSections.has(section.id) && (
                <div className="ersc-section-body">
                  {section.subsections.map(sub => {
                    const hasSubName = sub.name !== null;
                    if (!hasSubName) {
                      return sub.recommendations.map(rec => {
                        const isExpanded = expandedRecs.has(rec.id);
                        return (
                          <div key={rec.id} className="ersc-recommendation">
                            <button
                              className={`ersc-rec-header ${isExpanded ? 'expanded' : ''}`}
                              onClick={() => toggleRec(rec.id)}
                              aria-expanded={isExpanded}
                            >
                              <span className={`ersc-chevron${isExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
                              <span className="ersc-rec-id">{rec.id}</span>
                              <span className="ersc-rec-title">{rec.title}</span>
                              {rec.status && (
                                <span className={`ersc-status-badge small ${statusClass(rec.status)}`}>
                                  {statusLabel(rec.status)}
                                </span>
                              )}
                            </button>
                            {isExpanded && (
                              <div className="ersc-rec-body">
                                {renderRecMetadata(rec)}
                                {rec.description && (
                                  <p className="ersc-rec-desc">{rec.description}</p>
                                )}
                                {rec.subItems && rec.subItems.length > 0 && renderSubItems(rec.subItems)}
                              </div>
                            )}
                          </div>
                        );
                      });
                    }
                    const isSubExpanded = expandedSubsections.has(sub.id);
                    return (
                      <div key={sub.id} className="ersc-subsection">
                        <h3 className="ersc-heading"><button
                          className={`ersc-subsection-header ${isSubExpanded ? 'expanded' : ''}`}
                          onClick={() => toggleSubsection(sub.id)}
                          aria-expanded={isSubExpanded}
                        >
                          <span className={`ersc-chevron${isSubExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
                          <span className="ersc-subsection-name">{sub.name}</span>
                        </button></h3>
                        {isSubExpanded && (
                          <div className="ersc-subsection-body">
                            {sub.recommendations.map(rec => {
                              const isExpanded = expandedRecs.has(rec.id);
                              return (
                                <div key={rec.id} className="ersc-recommendation">
                                  <button
                                    className={`ersc-rec-header ${isExpanded ? 'expanded' : ''}`}
                                    onClick={() => toggleRec(rec.id)}
                                    aria-expanded={isExpanded}
                                  >
                                    <span className={`ersc-chevron${isExpanded ? ' expanded' : ''}`} aria-hidden="true"></span>
                                    <span className="ersc-rec-id">{rec.id}</span>
                                    <span className="ersc-rec-title">{rec.title}</span>
                                    {rec.status && (
                                      <span className={`ersc-status-badge small ${statusClass(rec.status)}`}>
                                        {statusLabel(rec.status)}
                                      </span>
                                    )}
                                  </button>
                                  {isExpanded && (
                                    <div className="ersc-rec-body">
                                      {renderRecMetadata(rec)}
                                      {rec.description && (
                                        <p className="ersc-rec-desc">{rec.description}</p>
                                      )}
                                      {rec.subItems && rec.subItems.length > 0 && renderSubItems(rec.subItems)}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="ersc-source">
          <p>{erscData.source}</p>
        </div>

        <DisclaimerFooter>
          <p>
            For source information from the Town of Brookline, go to the{' '}
            <a href="https://www.brooklinema.gov/3899/Expenditures-Revenues-Study-Committee" target="_blank" rel="noopener noreferrer">
              Expenditures &amp; Revenues Study Committee<span className="visually-hidden"> (opens in new tab)</span>
            </a>
            {' '}web page on the Town of Brookline website.
          </p>
        </DisclaimerFooter>
      </main>
    </div>
  );
}

export default ErscRecommendations;
